"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CURRENT_DISCOVERY_RUBRIC_VERSION = exports.CURRENT_RUBRIC_VERSION = void 0;
/**
 * The rubric version stamped on every new score. SINGLE SOURCE OF TRUTH used by
 * BOTH the score-persister (stamps it on the persisted item + folds it into the
 * cache key) and the bedrock-evaluator (folds it into the pre-evaluation lookup
 * key), so the lookup key and the stamped key are identical for the same inputs
 * (score-cache NFR-3 key correctness). `'v3'` = the Constitution-aware rubrics.
 * `'v4'` = framework-neutral traceability (additive `ScanData` fields; legacy
 * v1/v2/v3 rows and read paths are unaffected).
 *
 * `'v5'` and `'v6'` were each claimed independently by two sibling slices
 * developed concurrently on unmerged branches — `'v5'` = framework-neutral
 * supplemental context (spec #472; additive `authoredContextDocs`/
 * `docsCorpus`/`externalContextRefs` `ConfigScanInfo` fields + the
 * SUPPLEMENTAL CONTEXT recommendation-discipline rubric rewrite) and `'v6'` =
 * framework-neutral AI-collaboration (additive `aiAttributedCommits`/
 * `aiAttributedRatio`/`aiReviewBots`/`aiToolConfigs` fields + the
 * AI-COLLABORATION recommendation-discipline rubric rewrite). Neither was
 * ever the sole `CURRENT_RUBRIC_VERSION` on `main` — `'v6'` merged to `main`
 * first (making it briefly current there), then this merge combines both
 * slices' rubric-affecting changes for the first time. `'v7'` = both
 * combined — the correct next bump, since `'v6'` alone (as shipped on
 * `main`) does not yet reflect the supplemental-context rubric rewrite, so
 * reusing it here would let a stale cached score survive the exact kind of
 * rubric-text change score-cache FR-5 exists to invalidate. Legacy v1–v6
 * rows and read paths are unaffected.
 *
 * `'v8'` = framework-neutral Feedback Loop (spec #517; the any-mechanism
 * feedback-loop rubric rewrite that decouples L4/L5 from the agent-rules file
 * + the FEEDBACK LOOP recommendation-discipline rule + additive
 * `GitScanInfo.reviewStats` / `ConfigScanInfo.feedbackIssues` fields). The
 * rubric-text change materially shifts Feedback-Loop scores, so the bump is
 * required to invalidate any cached score predating it (score-cache FR-5).
 * Legacy v1–v7 rows and read paths are unaffected.
 *
 * `'v9'` = verified-AC traceability (verified-ac-traceability spec #508). The
 * Artifact-Traceability rubric now gates L4/L5 on the additive
 * `ScanData.verifiedAcTraceability` signal (L4 = a walkable chain consistent
 * across the latest 3–5 features via in-repo AC-defined OR consistent external
 * linkage; L5 = in-repo AC-verified evidence repeatable across those features).
 * The #459 mechanism-neutral L1–L3 base is unchanged and the change is
 * additive-only / never-penalize, but it IS a scoring change — a repo can now
 * reach L4/L5 on evidence prior scores could not see — so it must bump the
 * shared version to force a global cache miss (score-cache FR-5). This slice
 * originally bumped to `'v8'`, but `'v8'` was taken by the Feedback Loop slice
 * (#517) that merged to `main` first; two distinct rubric changes cannot share
 * a version, so this rebase advances it to `'v9'`. Legacy v1–v8 rows and read
 * paths are unaffected.
 *
 * `'v10'` = impact-ranked, variable-count top recommendations
 * (impact-ranked-recommendations spec #525). The bedrock-evaluator now ranks
 * recommendations by their `impact` field, drops any below the
 * `MIN_RECOMMENDATION_IMPACT` bar, caps the list at `MAX_RECOMMENDATIONS`, and
 * re-stamps `priority` to the impact rank — and the evaluator prompt permits a
 * dimension to emit zero recommendations. The set of recommendations a report
 * surfaces (their count, ordering, and `priority` meaning) therefore changes
 * materially, so the shared version bumps to force a global cache miss
 * (score-cache FR-5) rather than let a prior-rubric cached score survive. The
 * scoring dimensions/weights/floor rule are unchanged. Legacy v1–v9 rows and
 * read paths are unaffected.
 *
 * `'v11'` = corrected `acDefined` signal, defined from spec files
 * (verified-ac-defined-from-spec-files spec #528). This is a scan-signal-only
 * change on its face, but the exact-key score cache is keyed on
 * `scannedSha + rubricVersion + resolvedImports` and does NOT include
 * `verifiedAcTraceability` — so without a bump, the exact-key cache would
 * serve stale scores computed against the old (uncorrected) `acDefined`
 * signal that feeds the Artifact-Traceability L5 gate (the #505 failure
 * mode: a scoring-relevant change without a version bump serves stale
 * cache). This slice originally bumped to `'v10'`, but `'v10'` was taken by
 * the impact-ranked-recommendations slice (#525) that merged to `main`
 * first; two distinct rubric changes cannot share a version, so this rebase
 * advances it to `'v11'`. Bumping forces a global cache miss (score-cache
 * FR-5) so the fleet re-evaluates. Legacy v1–v10 rows and read paths are
 * unaffected.
 *
 * `'v12'` = a SINGLE bump covering two concurrent slices from spec #540: FR-1
 * (PR↔branch linkage feeding the Feedback-Loop score — a scoring change) AND
 * FR-2 (root-cause + examples recommendation provenance — report-only, omitted
 * from the exact-key cache key). Because FR-1 changes scoring, one bump forces
 * the fleet to re-evaluate; FR-2 rides the same bump so recommendations
 * re-consolidate at the same time. Both consumers inherit `'v12'` via the
 * shared import — no per-Lambda literal (constitution "shared cross-lambda
 * constants" + "rubric-version discipline"). Legacy v1–v11 rows and read paths
 * are unaffected.
 *
 * `'v13'` = traceability-rec-requirement-identifier (spec #560) — a
 * rec-guidance-only correction: the Artifact-Traceability
 * TRACEABILITY RECOMMENDATION DISCIPLINE prompt clause now explicitly
 * requires recommendations to cite the requirement/intent identifier
 * (issue/ticket/user-story/spec ID) as the linking key, and forbids
 * prescribing a tool-specific internal task-tracker ID (e.g. a "bead ID")
 * as the citation target — a live Bedrock recommendation had suggested
 * "reference bead IDs" in PR descriptions/branch names, hardcoding one
 * tool's internal tracker as the link. Recommendations are report-only and
 * omitted from the exact-key score cache, so this change alone would never
 * force re-evaluation without a bump — the bump is what forces the fleet to
 * regenerate recommendations under the corrected guidance. No scoring
 * dimension, weight, or floor rule changes. Legacy v1–v12 rows and read
 * paths are unaffected.
 *
 * `'v14'` = tracker-neutral-recommendations (spec #564) — a rec-guidance-only
 * tightening: the global "no framework/tool/product name" prohibition now
 * explicitly forbids citing a tool/product name EVEN AS AN ILLUSTRATIVE
 * EXAMPLE (a linking-key/commit-trailer example must use a generic
 * placeholder, e.g. `Closes #<N>`, never a real tool name), and the
 * discipline is stated to apply to ALL per-dimension recommendation blocks
 * (Constitution, Traceability, Supplemental Context, AI-Collaboration,
 * Feedback Loop) — not traceability alone. The Feedback Loop
 * recommendation-discipline block is also tightened to be explicitly
 * tool-neutral (no "beads"/tracker-product name even as an example).
 * Verifying #563 surfaced live Bedrock output naming a tool ("Resolves bead
 * XYZ") even though no such literal string exists in the prompt text — the
 * fix tightens the guidance so the model stops improvising a tool-specific
 * example. Recommendations are report-only and omitted from the exact-key
 * score cache, so this change alone would never force re-evaluation without
 * a bump — the bump is what forces the fleet to regenerate recommendations
 * under the tightened guidance. No scoring-rubric text changed. Legacy
 * v1–v13 rows and read paths are unaffected.
 *
 * `'v15'` = recommendation-framing-and-clustering (spec #569) — a
 * rec-guidance + consolidation-only change in three parts, none of which
 * touches a scoring dimension, weight, or floor rule: (FR-1) the general
 * actionable-rec example + the traceability guidance are rewritten to seed a
 * chain/PR-level mental model (a squash/bundle framework references the
 * requirement at the PR/commit level; a task-tracker item is a pointer *to*
 * the requirement, not a per-commit ID to stamp on every commit) and the
 * now-redundant per-commit "don't" caveats in the traceability discipline
 * block are trimmed; (FR-2) the "action verb" mandate is reframed to
 * advisory-with-payoff, with the payoff framed to match the rec's `impact`
 * tier (high = level jump, medium = solidify at the current level) rather
 * than a forced Lx→Ly on every rec; (FR-3) the Haiku consolidation prompt
 * names a dimension-agnostic "process/framework adopted but applied
 * inconsistently" mergeable cluster so cross-dimension process-adoption recs
 * merge while genuinely-distinct recs stay separate. Recommendations are
 * report-only and omitted from the exact-key score cache, so this change
 * alone would never force re-evaluation without a bump — the bump is what
 * forces the fleet to regenerate + re-consolidate recommendations under the
 * new framing. Legacy v1–v14 rows and read paths are unaffected.
 *
 * `'v16'` = repo-attributed, non-deficiency-coded recommendation wording (spec
 * #615) — a rec-guidance-only correction: recommendation titles generated by
 * both the evaluator prompt and the Haiku consolidation prompt/tool text now
 * require an opportunity-framed action verb ("Extend...", "Add...",
 * "Author...") rather than a corrective command or bare restatement of the
 * shortfall; neither titles nor descriptions may use deficiency-coded words
 * (inconsistent(ly), lacking, missing, fails to, doesn't, poor) — the
 * underlying metric carries the gap instead; and observational sentences are
 * anchored on the repo/codebase as subject, never "the team". The
 * consolidation named-mergeable-cluster label is also reworded (matching
 * semantics unchanged). Recommendations are report-only and omitted from the
 * exact-key score cache, so this change alone would never force
 * re-evaluation without a bump — the bump is what forces the fleet to
 * regenerate + re-consolidate recommendations under the new wording. No
 * scoring dimension, weight, or floor rule changes. Legacy v1–v15 rows and
 * read paths are unaffected.
 *
 * `'v17'` = Discovery recommendation wording parity
 * (discovery-recommendation-wording-parity) — a rec-guidance-only change on
 * the Discovery scoring path that brings its recommendation wording to parity
 * with the Delivery prompt: R2–R5 plus an adapted subject rule (R6′) are added
 * to the Discovery system prompt, a shared `DEFICIENCY_CODED_WORDS` list is
 * factored out and applied on both sides, and a deterministic tool-name scrub
 * runs over the Discovery path. Recommendations are report-only and omitted
 * from the exact-key score cache, so this change alone would never force
 * re-evaluation without a bump — the bump is what forces the fleet to
 * regenerate recommendations under the parity wording. No scoring dimension,
 * weight, or floor rule changes. Legacy v1–v16 rows and read paths are
 * unaffected.
 *
 * `'v18'` = level-tied recommendation floor (defect-nonl5-recommendation-floor)
 * — a rec-guidance-only change on the Delivery scoring path. The per-dimension
 * evaluator system prompt now carries a deterministic, level-tied recommendation
 * contract: for every dimension scored below L5 (score < 80) it emits at least
 * one actionable recommendation naming the concrete step toward the next level
 * (only an L5 dimension may emit zero), and the same evidence yields the same
 * recommendation set across runs — replacing the prior "emit ZERO for a
 * dimension that is already strong" guidance that let a below-L5 repo surface no
 * recommendations at all. Paired with a level-aware `rankRecommendations` floor
 * that keeps the single best recommendation for a non-L5 repo when nothing
 * clears the medium impact bar. Recommendations are report-only and omitted from
 * the exact-key score cache, so this change alone would never force
 * re-evaluation without a bump — the bump is what forces the fleet to regenerate
 * + re-consolidate recommendations under the new level-tied guidance. No scoring
 * dimension, weight, or floor rule changes. Legacy v1–v17 rows and read paths
 * are unaffected.
 *
 * `'v19'` = broadened recommendation-consolidation grouping
 * (enhancement-rec-consolidation-grouping) — a rec-guidance-only change on the
 * Delivery scoring path. The Haiku consolidation prompt/tool now names two
 * additional mergeable clusters beyond the original adopted-but-partial-coverage
 * one: same-target-artifact (two or more recs whose action edits the same
 * artifact — e.g. several additions to the constitution — merge into one naming
 * that artifact) and not-yet-formalized (an existing practice not yet captured as
 * a discrete referenceable artifact). A merged rec takes the maximum impact of
 * its members and the highest-impact member's dimension, and its description is
 * progress-aware (naming what is already covered vs what remains). The
 * conservative default (preserve genuinely-distinct actions; no over-merge across
 * dimensions) and the no-vague-umbrella guardrail (rootCause + 2-3 concrete
 * examples) are retained. Recommendations are report-only and omitted from the
 * exact-key score cache, so this change alone would never force re-evaluation
 * without a bump — the bump is what forces the fleet to regenerate + re-consolidate
 * recommendations under the broadened grouping. No scoring dimension, weight, or
 * floor rule changes. Legacy v1–v18 rows and read paths are unaffected.
 *
 * `'v20'` = consolidation cluster-provenance guard (defect-consolidation-solo-rootcause)
 * — a rec-output correction on the Delivery scoring path. The v19 broadened grouping let the
 * model attach a cluster `rootCause` to a SOLO recommendation with fewer than 2 `examples` (a
 * "cluster of one"), which the parse persisted. `normalizeRec` now carries `rootCause` +
 * `examples` TOGETHER and ONLY for a genuine merge (a rootCause AND ≥2 concrete examples);
 * otherwise both are dropped and the recommendation survives as a plain, actionable rec — a
 * deterministic backstop mirroring `scrubRecommendations`, with the consolidation prompt/tool
 * tightened to say the same. Recommendations are report-only and omitted from the exact-key
 * score cache, so this change alone would never force re-evaluation without a bump — the bump
 * is what forces the fleet to regenerate recommendations under the corrected provenance. No
 * scoring dimension, weight, or floor rule changes. Legacy v1–v19 rows and read paths are
 * unaffected.
 *
 * `'v21'` = tightened commit-classification patterns (classifier-bareword-plan-spec) — a bare
 * `plan`/`spec` word in a commit message is no longer SDD provenance on ANY path; only the
 * `plan:`/`spec:` reference forms are. `\b` treats `-` as a non-word character, so the old bare
 * forms matched the `plan` inside `terraform-plan-apply` and credited routine config commits like
 * `Update terraform-plan-apply-np.yml` as SDD-traced feature delivery. Three seams moved together:
 * (a) `SDD_PATTERNS`/`classifyCommit` (`classification.ts`) — such commits are now answered by the
 * verb taxonomy instead of short-circuiting to `feature`, so activity ratios shift for
 * config-heavy repos; (b) `buildGitInfo`'s `sddCommits`/`sddAdoptionDate`/`recent30Sdd`
 * (`scan.ts`), which feed the Bedrock prompt — including whether the Process-Consistency evidence
 * section renders at all, since `sddCommits > 0` is one of its `hasEvidence` disjuncts; and
 * (c) `bedrock-evaluator`'s `filter.ts` recompute for `mixed` repos. (b) and (c) had been
 * byte-identical private copies of the same regex in two Lambdas; they are now ONE declaration,
 * `hasSddProvenance` in `@sdd-maturity/lambda-utils` (#1124). That consolidation also dropped a
 * bare `task` token those two copies carried alone (the reference form is `TASK-<n>`) and widened
 * their `TASK-\d`/`Bundle \d` to `\d+` — which fixes a real `Bundle 12` under-match and, more
 * importantly, prevents one: `TASK-12` used to match only via that bare `task` token, so dropping
 * it without widening would have silently stopped crediting multi-digit task ids. STILL UNCHANGED, tracked on #1120:
 * the bare tool names `sdd`/`speads`/`beads` remain provenance signals. This bump is load-bearing
 * rather than bookkeeping:
 * commit classification is NOT part of the exact-key cache material (`scannedSha +
 * rubricVersion + resolvedImports`), and the other two inputs are properties of the scanned
 * repo, so for every repo whose HEAD has not moved the corrected classifier would produce a
 * byte-identical key, hit the cache, and keep serving the pre-fix score indefinitely. The
 * version is the only input this change can move, and moving it is what makes the fix
 * observable on already-scored repos. Activity ratios shift for config-heavy repos as a
 * result; no scoring dimension, weight, or floor rule changes. Legacy v1–v20 rows and read
 * paths are unaffected.
 *
 * `'v22'` = context-doc excerpts to the evaluator; heading-keyword gate dropped
 * (context-doc-excerpts-to-evaluator) — a prompt-INPUT change on the Delivery
 * scoring path, not a rubric-text rewrite: the repo-scanner's heading-keyword
 * gate (`artifact-detector.ts`) that previously suppressed context-doc detection
 * unless a recognized heading phrase matched is deleted in favor of a small stub
 * floor, and bounded excerpts of the already-fetched context-doc content now
 * flow through to the evaluator prompt (plus a `contextDocsDegraded` flag on a
 * failed/partial read). Excerpt content is NOT part of the exact-key score
 * cache (`cache-key.ts:80-98` keys on `scannedSha + rubricVersion +
 * resolvedImports` + the degrade flags — never the doc bytes themselves), so
 * without this bump a repo whose HEAD hasn't moved would keep hitting the
 * cache and never see the corrected Supplemental-Context evidence — the same
 * #505 / `'v11'` failure mode (a scan-signal-only change on its face that the
 * exact-key cache cannot observe on its own). No scoring dimension, weight, or
 * floor rule changes. Legacy v1–v21 rows and read paths are unaffected.
 *
 * `'v23'` = `bedrock-evaluator`'s `filterScanDataForFeatureCommits` (spec #1108) now
 * recomputes `traceabilityLinkedCommits`/`traceabilityLinkedRatio`/`aiAttributedCommits`/
 * `aiAttributedRatio` from the FILTERED feature-commits-only population for `mixed`
 * repos, instead of passing them through unfiltered via `...scanData.git` beside a
 * `totalCommits` narrowed to feature commits — previously internally-inconsistent
 * evidence for the Artifact Traceability and AI Collaboration dimensions (e.g. "Total
 * commits: 3" beside "Traceability-linked commits: 12"). `prBranchLinkage` is also now
 * forced to `{status:'unknown'}` for `mixed` repos, since it is PR-derived (no per-commit
 * source) and its `'collected'` counts, if any, describe the unfiltered population. Same
 * cache-staleness failure mode as the `'v21'` bump above: commit filtering for `mixed`
 * repos is not part of the exact-key cache material, so an unversioned change would serve
 * stale, internally-inconsistent evidence indefinitely for already-scored `mixed` repos.
 * No scoring dimension, weight, or floor rule changes. Legacy v1–v22 rows and read paths
 * are unaffected.
 *
 * `'v24'` = path-audit line-count widened `active-no-sdd` demote (spec
 * #1107, path-audit-line-counts) — a scan-signal-only change on its face,
 * the same framing as `'v11'`/`'v22'`. `activityClassificationV2`'s
 * `active-no-sdd` → `maintenance-only` demote clause now also fires on a
 * zero-authored-line window (pure renames/config churn inside `iac`/`helm`-
 * classified files), not just on `pathAudit.verdict === 'all-ktlo'` —
 * `iac`/`helm` touches keep `verdict` at `mixed` (never `all-ktlo`; see
 * `path-audit.ts`'s `TRIVIAL_KTLO_CATEGORIES`), so the pre-existing branch
 * alone missed this case. A repo the fleet previously scored as
 * `active-no-sdd` can now classify as `maintenance-only` and be skipped
 * instead, and the exact-key score cache (`scannedSha + rubricVersion +
 * resolvedImports`) does not observe `activityClassificationV2` directly,
 * so without a bump it would keep serving the pre-fix `active-no-sdd`
 * score/classification for an unmoved HEAD. This is a SEPARATE
 * scan-signal-only change from `'v22'`'s and `'v23'`'s (different slice,
 * different scan signal), so it claims its own slot rather than folding into
 * either — both `'v22'` (PR #1146) and `'v23'` (PR #1148, spec #1108) had
 * already shipped to `main` by the time this slice rebased onto it, so
 * `'v24'` is simply the next free slot after both, not a raced-for number
 * (this slice originally claimed `'v23'` during execution, before #1148
 * merged and took it — renamed here on rebase, mirroring the `'v9'`/`'v10'`/
 * `'v11'` precedent above). No scoring dimension, weight, or floor rule
 * changes. Legacy v1–v23 rows and read paths are unaffected.
 *
 * `'v26'` = complexity admission gate (spec #1109) — the evidence
 * classification / path-audit population this gate adds is not part of the
 * exact-key score cache (`scannedSha + rubricVersion + resolvedImports` +
 * degrade flags), so without this bump a repo whose HEAD hasn't moved would
 * keep hitting the cache and never see the corrected admit/demote decision —
 * the same #505 / `'v11'` failure mode. This slice originally claimed `'v25'`
 * during execution, but an unmerged sibling branch
 * (`enhancement/context-doc-excerpts-dev-verification`, spec #1106) independently
 * claimed `'v25'` first on `origin` for an unrelated reason — renamed to `'v26'`
 * to avoid the race, mirroring the `'v9'`/`'v10'`/`'v11'` and `'v23'`/`'v24'`
 * precedents above. No scoring dimension, weight, or floor rule changes. Legacy
 * v1–v24 rows and read paths are unaffected.
 *
 * `'v27'` = task-decomposition evidence for spec-kit / spec-driven-skills /
 * story-sharded repos. `files.tasks[]` — the only task signal that reaches the
 * evaluator prompt — was globbed from the speads/beads paths only, so every
 * repo on those three frameworks reported "Tasks: none" no matter how thorough
 * its real decomposition was (~71 active repos on live dev data). This bump is
 * required for the same reason `'v26'` was: the fix changes a PROMPT INPUT, not
 * the persisted `scannedSha`, so without it a repo whose HEAD hasn't moved keeps
 * hitting the exact-key cache and never sees its corrected evidence. No scoring
 * dimension, weight, or floor rule changes. Legacy v1–v26 rows and read paths
 * are unaffected.
 *
 * `'v28'` = stop scoring an ABSENT PROXY as a gap (#1190). Two prompt-wording
 * fixes: (a) the feedback-issues evidence line rendered a bare `none` while all
 * four sibling absence lines carry an "absence is NOT a gap" caveat, so an unused
 * label channel read as an observed zero — a repo whose improvement loop lives in
 * versioned constitution amendments was told to add `lesson` labels it does not
 * use; (b) the supplemental-context clause banned artifact-type prescription
 * abstractly but not in the `title`, and 14 of 15 ADR/glossary recommendations on
 * live dev data prescribed a NEW artifact anyway — one of them for a repo that
 * already had `GLOSSARY.md`. Required for the same reason as `'v26'`/`'v27'`:
 * recommendations are excluded from the exact-key cache's CONTENT hash
 * (`docs/constitution.md:530`), so without a bump a repo whose HEAD hasn't moved
 * keeps hitting the cache and serves pre-fix wording indefinitely. No scoring
 * dimension, weight, or floor rule changes. Legacy v1–v27 rows and read paths are
 * unaffected.
 *
 * `'v29'` = TITLE FRAMING constrains the OBJECT, not just the verb (#1194). The
 * v28 fix above worked on descriptions but leaked in `title`s: live dev
 * verification of v28 (20 repos re-scanned) found the banned shape fell from
 * 16/20 to 3/16, and all 3 survivors were L2 repos WITH existing context whose
 * descriptions correctly said "add a rationale section to the existing README"
 * while the title still read "Add architecture rationale section to README or
 * companion ARCHITECTURE.md". Root cause was instruction PRECEDENCE, not a
 * missing prohibition — TITLE FRAMING explicitly whitelists the verb "Add..."
 * several clauses before the supplemental block bans artifact nouns, and the
 * model honored the nearer, more concrete whitelist. So v29 does NOT add a
 * seventh prohibition (the lever that produced this leak); it changes the rule's
 * subject from the verb to its grammatical OBJECT and supplies worked
 * WRONG/RIGHT pairs, including the two titles observed on live dev data. Bumped
 * for the same cache reason as `'v26'`–`'v28'`: recommendations are excluded from
 * the exact-key cache's CONTENT hash (`docs/constitution.md:530`), so an
 * unbumped repo at an unchanged HEAD would serve pre-fix titles indefinitely. No
 * scoring dimension, weight, or floor rule changes — this governs `title`
 * phrasing only, so scores are expected to be stable across the bump (v28's own
 * verification measured a same-sha feedback-loop mean delta of -0.7).
 *
 * `'v30'` = ban the GENERIC document head noun (#1194, second iteration). v29
 * reframed TITLE FRAMING from the verb to its grammatical object and fixed the
 * concrete cases (`cc8-theme` became "Extend README with architectural
 * rationale"), but dev re-verification at identical `scannedSha` showed one
 * shape still leaking: "Add architectural and domain DOCUMENTATION to the README
 * or companion artifact". v29's banned list named `README`, `ADR`, `glossary` and
 * the pattern `<X> document/doc/file` — but not BARE `documentation` or
 * `artifact`, so a generic head noun carrying the real outcome words demoted to
 * adjectives passed the test. v30 bans the head nouns outright (documentation,
 * docs, doc, document, artifact, file, page, ...) with an explicit "adjectives do
 * not rescue a document kind" instruction, supplies the observed miss as a third
 * WRONG example, and adds two RIGHT examples that pin the distinction the rule
 * previously left implicit: a document kind is FINE as a prepositional
 * destination ("Extend README WITH rationale", "Add a rationale section TO the
 * README") and only banned as the head noun of the thing being gained. Same cache
 * rationale as `'v26'`-`'v29'` (`docs/constitution.md:530`). No scoring
 * dimension, weight, or floor rule changes — v29's same-sha verification measured
 * a supplemental-context mean delta of +0.0 across 8 repos.
 *
 * `'v31'` = recent-practice coverage + framework-neutral verification breadth.
 * The first entry in a while that DOES change scoring behaviour, in three ways:
 *   1. Verified-AC dispatch is EVIDENCE-triggered, not framework-tag-keyed
 *      (#1122). Previously only four registered tags ran detectors, so `bmad`,
 *      `custom-partial` and `none` — the majority of scanned repos — reported
 *      `acDefined: 0` regardless of contents and could never reach L5.
 *   2. Verification breadth is measured in FEATURES COVERED, not proof-artifact
 *      count. The artifact count's unit differs per framework (one file per
 *      feature for a per-slice verdict vs one file total for a wired CI gate),
 *      and the model anchored the L5 band on it: holding breadth and proof kind
 *      fixed while changing only the count 1 -> 14 moved traceability 75 -> 90,
 *      while upgrading the proof KIND at count 1 moved it not at all.
 *   3. Recent-practice coverage now CEILINGS Artifact Traceability and Process
 *      Consistency. Verified-AC evidence is read from the tree at HEAD and
 *      carries no recency, so a team that practised SDD a year ago and stopped
 *      scored identically to one practising this week and a score could only
 *      ratchet up. Coverage is scoped to features touched inside the window;
 *      lapsed practice caps at L3. Truncated reads emit NO coverage section
 *      (a visible-but-caveated ratio was measured to down-score a practising
 *      repo from L5 to L4 anyway).
 * Weights, the six dimensions and the floor rule are UNCHANGED. Same-sha
 * verification on four repos: arb-bff 78/L4 -> 83/L5 (spec-kit, previously
 * ceiling-locked), FeeScheduler-API 81/L5 -> 82/L5, orbit-facilitation-api
 * 84/L5 -> 83/L5, pat-ui 82/L5 -> 81/L5; synthetic lapse on arb-bff drops
 * traceability 90 -> 55. Forces a global cache miss, by design.
 */
exports.CURRENT_RUBRIC_VERSION = 'v31';
/**
 * The Discovery rubric version folded into the Discovery cache key
 * (`{ featureId, lastUpdateDate, epicAttachmentSignature, discoveryRubricVersion }`)
 * and stamped on every new Discovery score. SINGLE SOURCE OF TRUTH imported by
 * BOTH the Discovery cache lookup and the Discovery stamp — never re-declared as
 * a per-Lambda literal (constitution "shared cross-lambda constants" rule), so
 * the lookup key and the stamped key are identical for the same inputs.
 *
 * DEDICATED — NOT an alias of `CURRENT_RUBRIC_VERSION` (CQ-1). The Discovery and
 * Delivery scoring paths, rubrics, and recommendation prompts evolve
 * independently, so binding the Discovery cache key to Delivery's version would
 * force spurious full Discovery re-scores on every Delivery-only bump (and vice
 * versa). Bumping THIS constant (a Discovery scoring or recommendation-wording
 * change) is what invalidates the Discovery cache and forces the fleet to
 * re-score under the new rubric (discovery-score-cache FR-5; constitution "bump
 * the rubric version on scoring/wording change").
 *
 * `'d-v1'` = the Discovery rubric baseline — the "Measuring AI-Transformed
 * Discovery" 6-dimension rubric as it stands when the Discovery score cache is
 * introduced (discovery-score-cache). Its introduction changes no Discovery
 * scoring dimension, weight, or floor rule; it pins the starting version the
 * Discovery cache key is keyed on. Each subsequent Discovery scoring/wording
 * change appends a `'d-vN'` union member and advances this constant.
 *
 * `'d-v2'` = structured Discovery recommendation (enhancement-discovery-rec-structured-title)
 * — a rec-shape/wording change on the Discovery path. Each Discovery recommendation becomes a
 * structured `DiscoveryRecommendation` (an opportunity-framed `title` + `description`, replacing
 * the prior bare string), reaching format parity with Delivery and rendering through the same
 * dashboard component. Discovery recommendations are report-only and omitted from the exact-key
 * Discovery score-cache content hash, so this change alone would never force re-evaluation
 * without a bump — the bump is what forces the fleet to regenerate Discovery scores under the
 * structured shape. No Discovery scoring dimension, weight, or level rule changes. Legacy `'d-v1'`
 * string-recommendation rows and read paths are unaffected (the shape is a legacy-tolerant union).
 *
 * `'d-v3'` = grouped + capped top-level Discovery recommendation set
 * (enhancement-discovery-rec-grouping-cap) — adds a top-level, grouped and capped
 * `recommendations[]` to the Discovery score (parity with Delivery's
 * `assessment.recommendations[]`), populated by a consolidation pass, additive to
 * and separate from the per-dimension `dimensions[].recommendation`. Discovery
 * recommendations are report-only and omitted from the exact-key Discovery
 * score-cache content hash, so this change alone would never force re-evaluation
 * without a bump — the bump is what forces the fleet to regenerate the grouped
 * Discovery recommendation set. No Discovery scoring dimension, weight, or level
 * rule changes. Legacy `'d-v1'`/`'d-v2'` rows and read paths are unaffected (the
 * field is optional/additive).
 *
 * `'d-v4'` = 3 of the 6 Discovery dimensions (Hypothesis Validation Rigor, Solution
 * Option Exploration, User Validation Depth) taken OUT of scoring (`scored: false`
 * in `discovery-rubric.ts`) — Rally Features don't reliably carry the source
 * artifacts those dimensions need, and scoring them against absent artifacts
 * produced a generic, non-discriminating L1 result rather than a real maturity
 * signal (confirmed against prod score history 2026-08-17). The Bedrock tool
 * schema, system/user prompts, and parser now ask for and validate exactly the 3
 * remaining dimensions; `computeWeightedScore`/`applyFloorRule` re-normalize over
 * those 3 (their weights sum to 50 of the original 100, renormalized to 100%). A
 * prior cached score's `dimensions[]` (6 entries, under `'d-v1'..'d-v3'`) is no
 * longer comparable to a `'d-v4'` score's `dimensions[]` (3 entries) — this bump
 * forces the fleet to regenerate under the 3-dimension shape. Their rubric
 * definitions/weights/levels stay in `DISCOVERY_RUBRIC` unchanged (never deleted),
 * so re-enabling a dimension is a one-line `scored` flip plus a further version
 * bump, not a re-derivation. This bump does NOT touch `CURRENT_RUBRIC_VERSION`
 * (Delivery) — see this constant's own doc comment (CQ-1) for why the two never
 * cross-invalidate.
 *
 * `'d-v5'` = the exact-key Discovery score-cache's change signal switched from
 * the Feature's Rally `LastUpdateDate` to a content digest over `description` +
 * `notes` (`deriveFeatureContentSignature`, `@sdd-maturity/lambda-utils`).
 * `LastUpdateDate` bumps on ANY Feature field edit — including ones no scored
 * dimension reads, e.g. a story-point estimate — so keying on it forced a
 * needless Bedrock re-score for content that never actually changed. No
 * scoring dimension, weight, or level rule changes; this bump forces one
 * fleet-wide re-score purely to re-key every cached score under the new
 * signal (every prior key is `'d-v4'`-or-earlier and can never collide with a
 * `'d-v5'` key by construction, since the rubric version is itself part of the
 * hashed material — but the OLD keys were derived from `lastUpdateDate`, which
 * no longer matches what a `'d-v5'` lookup computes, so they would never hit
 * regardless; the bump makes that miss deliberate and visible rather than an
 * accidental byproduct). `lastUpdateDate` remains persisted on every row,
 * unchanged — it now serves ONLY the dashboard staleness display (#1251) and
 * is no longer a cache-key input.
 */
exports.CURRENT_DISCOVERY_RUBRIC_VERSION = 'd-v5';
//# sourceMappingURL=rubric.js.map